import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { VictoryChart, VictoryLine, VictoryAxis, VictoryContainer } from 'victory'

const strokeDasharray = '6, 3'
const stroke = 'rgba(0, 0, 0, 0.1)'
const axis = { strokeDasharray, stroke }
const tickLabels = {
    fontSize: 12,
    fill: '#A0A0A0'
}
const axiosXNum = 24

function MobileActivityTrend({ detail = {} }) {
    const [data, setData] = useState([])
    const [axiosY, setAxiosY] = useState([])
    const [axiosX, setAxiosX] = useState([])
    const [zoneColor, setZoneColor] = useState('')
    const [xMax, setXMax] = useState(0)
    const [xStemp, setXStemp] = useState(1)

    useEffect(() => {
        const samples = Array.isArray(detail?.samples) ? detail.samples : []
        const heartRateZoneSource = Array.isArray(detail?.heartRateZoneSource)
            ? detail.heartRateZoneSource.map((v) => Number(v) || 0)
            : []

        if (!samples.length) {
            if (data.length !== 1 || data[0].x !== 0 || data[0].y !== 0) {
                setData([{ x: 0, y: 0 }])
            }
            return
        }

        const zoneColors = [
            'rgba(255,107,0,.8)',
            'rgba(255,149,51,.7)',
            'rgba(255,107,0,.8)',
            'rgba(255,88,51,.9)',
            '#FF3D00'
        ]
        const colorIndex = heartRateZoneSource.findIndex((item) => item > 0)
        if (colorIndex !== -1 && zoneColors[colorIndex] !== zoneColor) {
            setZoneColor(zoneColors[colorIndex])
        }

        const startTime = Number(samples[0][0]) || 0
        const mappedData = samples.map(([time, rate]) => ({
            x: Number(time) - startTime,
            y: Number(rate) || 0
        }))

        const yValues = mappedData.map((d) => d.y).filter(Boolean)
        const yMin = yValues.length ? Math.floor(Math.min(...yValues) / 10) * 10 : 0
        let yMax = yValues.length ? Math.ceil(Math.max(...yValues) / 10) * 10 : 10
        if (yMax === yMin) yMax += 10

        const yTicks = [yMin, (yMin + yMax) / 2, yMax]

        const xMaxVal = Math.max(...mappedData.map((d) => d.x), 0)
        const step = Math.ceil(Math.max(xMaxVal / axiosXNum, 1))
        const xTicks = Array.from({ length: axiosXNum }, (_, i) => i * step)

        // Only update states if values have changed
        if (JSON.stringify(data) !== JSON.stringify(mappedData)) {
            setData(mappedData)
        }
        if (JSON.stringify(axiosY) !== JSON.stringify(yTicks)) {
            setAxiosY(yTicks)
        }
        if (JSON.stringify(axiosX) !== JSON.stringify(xTicks)) {
            setAxiosX(xTicks)
        }
        if (xMax !== xMaxVal) {
            setXMax(xMaxVal)
        }
        if (xStemp !== step) {
            setXStemp(step)
        }
    }, [detail]) // Only run when `detail` changes

    const formatXAxis = (x) => {
        const safeX = Number(x) || 0
        if (xMax < 300) {
            const value = Math.round(safeX)
            return `0:${value < 10 ? '0' + value : value}`
        } else if (xMax < 3600) {
            const mins = Math.floor(safeX / 60)
            return `0:${mins < 10 ? '0' + mins : mins}`
        } else {
            const hrs = Math.floor(safeX / 3600)
            const mins = Math.floor((safeX % 3600) / 60)
            return `${hrs}:${mins < 10 ? '0' + mins : mins}`
        }
    }

    const getGridStroke = (x) =>
        ((Number(x) / xStemp) % 5 === 0 ? 'rgba(0,0,0,0.3)' : stroke)

    return (
        <div className="mobile-activity-trend">
            {data.length > 0 && (
                <VictoryChart
                    padding={{ top: 10, bottom: 30, left: 35, right: 10 }}
                    domainPadding={{ y: 10 }}
                    height={200}
                    width={350}
                    containerComponent={
                        <VictoryContainer
                            style={{
                                touchAction: 'pan-y',
                                pointerEvents: 'auto',
                                userSelect: 'auto'
                            }}
                        />
                      }
                >
                    <VictoryAxis
                        tickValues={axiosX}
                        tickFormat={(x) =>
                            x % (xStemp * 5) === 0 ? formatXAxis(x) : ''
                        }
                        style={{
                            axis,
                            grid: {
                                stroke: getGridStroke,
                                strokeDasharray
                            },
                            tickLabels
                        }}
                    />
                    <VictoryAxis
                        dependentAxis
                        tickValues={axiosY}
                        style={{
                            axis,
                            grid: {
                                stroke,
                                strokeDasharray
                            },
                            tickLabels
                        }}
                    />
                    <VictoryLine
                        data={data.filter((d) => typeof d.y === 'number')}
                        style={{
                            data: {
                                stroke: zoneColor || 'url(#linearGradient)',
                                strokeWidth: 2
                            }
                        }}
                    />
                </VictoryChart>
            )}
            <svg style={{ height: 0 }}>
                <defs>
                    <linearGradient id="linearGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#FF3D00" />
                        <stop offset="100%" stopColor="#FFB800" />
                    </linearGradient>
                </defs>
            </svg>
        </div>
    )
}

MobileActivityTrend.propTypes = {
    detail: PropTypes.object
}

export default MobileActivityTrend
